import { useEffect, useState } from "react";

// Time is ET first, Sydney second, always labelled.
const fmtCache = {};
function dtf(tz, opts) {
  const key = tz + JSON.stringify(opts);
  if (!fmtCache[key]) fmtCache[key] = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hourCycle: "h23", ...opts });
  return fmtCache[key];
}

/** A ticking Date. `interval` in ms. */
export function useNow(interval = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), interval);
    return () => clearInterval(t);
  }, [interval]);
  return now;
}

const toDate = (v) => (v instanceof Date ? v : v == null || v === "" ? null : new Date(v));

/** "10:01:00" in New York. `seconds: false` drops seconds. */
export function timeET(v, { seconds = true } = {}) {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return "—";
  return dtf("America/New_York", { hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}) }).format(d);
}
/** "00:01:00" in Sydney. */
export function timeSYD(v, { seconds = true } = {}) {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return "—";
  return dtf("Australia/Sydney", { hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}) }).format(d);
}
/** "Thu 20 Aug" — the Sydney calendar day. */
export function dayLabel(v, tz = "Australia/Sydney") {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return "";
  return dtf(tz, { weekday: "short", day: "numeric", month: "short" }).format(d).replace(",", "");
}
/** "10:01:00 ET · 00:01:00 AEDT": New York time, then the viewer's own local time (named by its zone). */
export function etSyd(v, { seconds = true } = {}) {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return "—";
  if (isLocalET()) return `${timeET(d, { seconds })} ET`;
  return `${timeET(d, { seconds })} ET · ${timeLocal(d, { seconds })} ${zoneAbbr(d)}`;
}
/** "2026-08-19 10:01:00 ET" */
export function dateTimeET(v, { seconds = true } = {}) {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return "—";
  const p = dtf("America/New_York", { year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const g = (t) => p.find((x) => x.type === t)?.value;
  return `${g("year")}-${g("month")}-${g("day")} ${timeET(d, { seconds })} ET`;
}
/** "Wed 19 Aug 2026" for a YYYY-MM-DD session date (no timezone shift). */
export function sessionDateLabel(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return iso || "—";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(dt).replace(",", "");
}
/** Seconds → "05:55:28" (or "55:28" under an hour). */
export function fmtDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

// ── New York time with the viewer's own local time beside it ─────────────────────────────────────────
// Every time shown in ET also shows the local equivalent (owner's request, 2026-10-16). "Local" is the
// browser's own zone (Sydney for the owner and the customer); when the viewer is in New York it is left out.
const ET_ZONE = "America/New_York";
export function localZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Australia/Sydney"; } catch { return "Australia/Sydney"; }
}
const isLocalET = () => localZone() === ET_ZONE;
const abbrFmt = {};
function zoneAbbr(d, tz = localZone()) {
  const loc = tz.startsWith("Australia/") ? "en-AU" : tz.startsWith("Europe/London") ? "en-GB" : "en-US";
  if (!abbrFmt[tz]) abbrFmt[tz] = new Intl.DateTimeFormat(loc, { timeZone: tz, timeZoneName: "short" });
  const z = abbrFmt[tz].formatToParts(d).find((p) => p.type === "timeZoneName")?.value || "";
  if (z && !/^(GMT|UTC)[+-]/.test(z)) return z;
  if (tz === "Australia/Sydney") return "SYD";
  return z || "local";
}
/** "01:01" (or "01:01:15") in the viewer's own zone. */
export function timeLocal(v, { seconds = true } = {}) {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return "—";
  return dtf(localZone(), { hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}) }).format(d);
}
const ymd = (d, tz) => {
  const p = dtf(tz, { year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const g = (t) => p.find((x) => x.type === t)?.value;
  return `${g("year")}-${g("month")}-${g("day")}`;
};
/** The instant at New York wall-clock `hhmm` ("10:01" or "10:01:15") on `day` (YYYY-MM-DD; default today in New York). */
export function wallET(hhmm, day) {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(hhmm || "").trim());
  if (!m) return null;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(day || "") ? day : ymd(new Date(), ET_ZONE);
  const [y, mo, d] = date.split("-").map(Number);
  const want = Date.UTC(y, mo - 1, d, Number(m[1]), Number(m[2]), Number(m[3] || 0));
  let t = want;
  for (let i = 0; i < 2; i++) {                       // shift by New York's offset at that instant (handles DST)
    const p = dtf(ET_ZONE, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(t));
    const g = (k) => Number(p.find((x) => x.type === k)?.value);
    const seen = Date.UTC(g("year"), g("month") - 1, g("day"), g("hour") % 24, g("minute"), g("second"));
    t += want - seen;
  }
  return new Date(t);
}
/** "01:01 AEDT" (+ " Sat" when the local day differs from the New York day) for a New York wall-clock time. */
export function localOf(hhmm, { day, seconds } = {}) {
  const d = wallET(hhmm, day);
  if (!d) return "";
  const secs = seconds ?? /:\d{2}:\d{2}$/.test(String(hhmm).trim());
  const shift = ymd(d, localZone()) !== ymd(d, ET_ZONE) ? ` ${dayLabel(d, localZone()).split(" ")[0]}` : "";
  return `${timeLocal(d, { seconds: secs })} ${zoneAbbr(d)}${shift}`;
}
/** "10:01 ET (01:01 AEDT)" for a New York wall-clock time; just "10:01 ET" for a viewer in New York. */
export function etAndLocal(hhmm, opts = {}) {
  if (!hhmm) return "—";
  const t = String(hhmm).trim();
  if (isLocalET() || !wallET(t, opts.day)) return `${t} ET`;
  return `${t} ET (${localOf(t, opts)})`;
}
/** Adds the local time to every "HH:MM ET" / "HH:MM:SS ET" / "HH:MM New York time" in a piece of text. */
export function withLocalTimes(text, { day } = {}) {
  if (!text || typeof text !== "string" || isLocalET()) return text;
  const d = /(\d{4}-\d{2}-\d{2})/.exec(text)?.[1] || day;
  const strip = (x) => x.replace(/ \S+( \S+)?$/, "");   // "01:15 AEDT Sat" -> "01:15"
  text = text.replace(/\b(\d{1,2}:\d{2})[–-](\d{1,2}:\d{2}) (ET|New York time)\b(?! \()/g, (all, a, b, label) => {
    const lb = localOf(b, { day: d });
    return `${a}–${b} ${label} (${strip(localOf(a, { day: d }))}–${lb})`;
  });
  return text.replace(/\b(\d{1,2}:\d{2}(?::\d{2})?) (ET|New York time)\b(?! \()/g, (all, t, label) => `${t} ${label} (${localOf(t, { day: d })})`);
}
/** The viewer's zone name at `d` ("AEDT"). */
export function localZoneAbbr(d = new Date()) { return zoneAbbr(d instanceof Date ? d : new Date(d)); }
/** A stored ET stamp: "HH:MM[:SS]" (with `day`) or a full date-time; returned with the local time beside it. */
export function etStamp(v, { day, seconds = true } = {}) {
  if (v == null || v === "") return "—";
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(String(v).trim())) return etAndLocal(String(v).trim(), { day });
  const d = toDate(v);
  return d && !Number.isNaN(d.getTime()) ? etSyd(d, { seconds }) : String(v);
}
